import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { NextIntlClientProvider } from "next-intl";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import {
  getLocale,
  setLocale as setLocaleInStore,
  subscribe as subscribeLocale,
} from "@/lib/i18n/localeStore";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  loadLocaleMessages,
  loadDefaultFloor,
  deepMerge,
  pickInitialLocale,
  type Messages,
} from "./locales";

type LocaleCtx = {
  locale: string;
  setLocale: (l: string) => void;
  supported: string[];
};

const Ctx = createContext<LocaleCtx | null>(null);

export function useLocale() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useLocale must be used inside <I18nProvider>");
  return c;
}

type Committed = { locale: string; messages: Messages };

/**
 * Loads the user's locale bundle via dynamic import and re-renders children
 * inside <NextIntlClientProvider>.
 *
 * Two-phase load so first paint isn't gated on the 563–607 KB en-GB fallback
 * floor: (1) load ONLY the active locale and render immediately; (2) for a
 * non-default locale, load the en-GB floor in the background and merge it under
 * the active locale to backfill any missing keys. During the brief gap,
 * next-intl renders the raw key for any not-yet-merged missing message
 * (getMessageFallback below), which is rare since locale files are complete.
 *
 * A locale SWAP keeps the previously committed bundle mounted until the new one
 * has loaded — `{locale, messages}` are committed as one pair. Blanking to a
 * full-screen spinner (what this used to do) unmounted the whole subtree below,
 * and since this provider sits ABOVE QueryClientProvider/AuthProvider in
 * main.tsx that meant a remount of the auth state machine and a second
 * `refreshAccessToken()` round trip on every language change.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useSyncExternalStore(subscribeLocale, getLocale, pickInitialLocale);
  const [committed, setCommitted] = useState<Committed | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Phase 1: active locale only → unblocks first paint.
    loadLocaleMessages(locale).then((active) => {
      if (cancelled) return;
      setCommitted({ locale, messages: active });

      // Phase 2: backfill the en-GB floor in the background (non-default only).
      if (locale !== DEFAULT_LOCALE) {
        loadDefaultFloor().then((floor) => {
          if (cancelled) return;
          // active keys win over the floor (deepMerge(base, over) → over wins).
          setCommitted({ locale, messages: deepMerge(floor, active) });
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  // Mirror the COMMITTED locale onto <html lang="…"> so screen-readers,
  // browser hyphenation, and Lighthouse a11y match the labels actually on
  // screen — not a language whose bundle is still in flight.
  useEffect(() => {
    if (typeof document === "undefined" || !committed) return;
    document.documentElement.lang = committed.locale;
  }, [committed]);

  const setLocale = useCallback((l: string) => {
    setLocaleInStore(l);
  }, []);

  const value = useMemo<LocaleCtx>(
    () => ({ locale, setLocale, supported: SUPPORTED_LOCALES }),
    [locale, setLocale],
  );

  // Only the very first load has nothing to show.
  if (!committed) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <Ctx.Provider value={value}>
      <NextIntlClientProvider
        locale={committed.locale}
        messages={committed.messages}
        timeZone="UTC"
        onError={(err) => {
          // Silence missing-key spam on dev; real errors still log.
          if (err.code === "MISSING_MESSAGE") {
            if (import.meta.env.DEV) console.warn(err.message);
            return;
          }
          console.error(err);
        }}
        getMessageFallback={({ key }) => key}
      >
        {children}
      </NextIntlClientProvider>
    </Ctx.Provider>
  );
}

export { DEFAULT_LOCALE, SUPPORTED_LOCALES };
